import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import dayjs, { OpUnitType } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isToday from "dayjs/plugin/isToday";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ref, computed, onMounted } from "vue";
import TimeSelect from "./selects/TimeSelect.vue";
import InlineSvg from "vue-inline-svg";
import { useRoute } from "vue-router";


const __default__ = {
  name: "Calendar",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  selectableFromDate: {
    type: String,
  },
  selectableToDate: {
    type: String,
  },
  initialSelectedDate: {
    type: String,
    default: dayjs(),
  },
  initialSelectedDateFormat: {
    type: String,
    default: "YYYY-MM-DD",
  },
  titleHeight: {
    type: String,
    default: "0px",
  },
  hasTimeSelect: {
    type: Boolean,
    default: false,
  },
  hideModalBackDrop: {
    type: Boolean,
    default: false,
  },
  hideTodayButton: {
    type: Boolean,
    default: false,
  },
},
  emits: ["onClickClose", "onSelectDate"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

_useCssVars(_ctx => ({
  "5b2f93e8-gridStartIndex": (gridStartIndex.value)
}))

dayjs.locale("ko");
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.tz.setDefault("Asia/Seoul");

const props = __props;

const emit = __emit;

const route = useRoute();

// 초기값 설정
const initialDate = props.initialSelectedDate.length
  ? dayjs(props.initialSelectedDate, props.initialSelectedDateFormat)
  : dayjs();
const year = ref(initialDate.year());
const month = ref(initialDate.month()); // 0 ~ 11
const selectedDate = ref<dayjs.Dayjs>(initialDate);

// 달력(월) 계산/렌더링
const formattedMonth = computed(() =>
  (month.value + 1).toString().padStart(2, "0")
);

// 달력(일) 계산/렌더링
const dates = computed(() => {
  const dateList = [];
  const firstDate = dayjs(new Date(year.value, month.value, 1));
  const daysInMonth = firstDate.daysInMonth();

  for (let i = 1; i <= daysInMonth; i++) {
    const dayJs = dayjs(new Date(year.value, month.value, i));
    dateList.push(dayJs);
  }

  return dateList;
});

const gridStartIndex = computed(() => dates.value[0].day() + 1);

const isSelectedDate = (date: dayjs.Dayjs) =>
  date.isSame(selectedDate.value, "day");

const isSelectable = (date: dayjs.Dayjs, unit = "day" as OpUnitType) => {
  if (!props.selectableFromDate && !props.selectableToDate) {
    return true;
  } else if (!props.selectableFromDate && props.selectableToDate) {
    return !date.isAfter(props.selectableToDate);
  } else if (props.selectableFromDate && !props.selectableToDate) {
    return !date.isBefore(props.selectableFromDate);
  }

  return date.isBetween(
    props.selectableFromDate,
    props.selectableToDate,
    unit,
    "[]"
  );
};

const isSelectableMonth = (setNext: boolean) => {
  const diff = setNext ? 1 : -1;
  const monthDayjs = dayjs(new Date(year.value, month.value, 1)).add(
    diff,
    "month"
  );
  return isSelectable(monthDayjs, "month");
};

const onClickToday = () => {
  const today = dayjs();
  const selectableFromDate = dayjs(props.selectableFromDate) ?? today;
  const selectableToDate = dayjs(props.selectableToDate) ?? today;
  if (!today.isBetween(selectableFromDate, selectableToDate, "day", "[]"))
    return;

  selectedDate.value = today;
  year.value = today.year();
  month.value = today.month();
};

const onClickDay = (date: dayjs.Dayjs) => {
  if (!isSelectable(date)) return;
  selectedDate.value = date;
};

const onSelectDate = () => {
  const dateStr = selectedDate.value.format(props.initialSelectedDateFormat);
  const date = props.hasTimeSelect
    ? `${dateStr} ${selectedTime.value}`
    : dateStr;
  emit("onSelectDate", date);
  emitOnClickClose();
};

const changeMonth = (setNext: boolean) => {
  if (!isSelectableMonth(setNext)) return;
  let newMonth = setNext ? month.value + 1 : month.value - 1;

  if (newMonth < 0) {
    month.value = 11;
    year.value -= 1;
    return;
  }
  if (newMonth > 11) {
    month.value = 0;
    year.value += 1;
    return;
  }

  month.value = newMonth;
};

const initialSelectedDate = computed(() =>
  props.initialSelectedDate.length
    ? props.initialSelectedDate
    : dayjs().format("YYYY-MM-DD")
);
const initialTime = dayjs(initialSelectedDate.value).format("HH:mm");
const selectedTime = ref(initialTime);
const onUpdateTime = (time: string) => {
  selectedTime.value = time;
};

const emitOnClickClose = () => {
  route.meta.onBackHandlerList.pop();
  emit("onClickClose");
};

onMounted(() => {
  route.meta.onBackHandlerList.push(emitOnClickClose);
});

const __returned__ = { props, emit, route, initialDate, year, month, selectedDate, formattedMonth, dates, gridStartIndex, isSelectedDate, isSelectable, isSelectableMonth, onClickToday, onClickDay, onSelectDate, changeMonth, initialSelectedDate, initialTime, selectedTime, onUpdateTime, emitOnClickClose, TimeSelect, get InlineSvg() { return InlineSvg } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})